import React from "react";
import { ColorPaletteProp } from "@mui/joy/styles";
import Box from "@mui/joy/Box";
import Chip from "@mui/joy/Chip";
import Link from "@mui/joy/Link";
import Table from "@mui/joy/Table";
import Sheet from "@mui/joy/Sheet";
import Checkbox from "@mui/joy/Checkbox";
import Typography from "@mui/joy/Typography";
import Tooltip from "@mui/joy/Tooltip";
import Stack from "@mui/joy/Stack";
import Card from "@mui/joy/Card";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonIcon from "@mui/icons-material/Person";
import SearchOffIcon from "@mui/icons-material/SearchOff";

import { CaseItem, Order } from "./types";
import { getComparator } from "./general";

interface TicketTableViewProps {
  rows: CaseItem[];
  selected: readonly string[];
  setSelected: React.Dispatch<React.SetStateAction<readonly string[]>>;
  order: Order;
  setOrder: React.Dispatch<React.SetStateAction<Order>>;
  onRowClick: (ticketId: string) => void;
  loading?: boolean;
}

// Helper function to format dates in a user-friendly way
const formatDate = (dateString: string): { formatted: string; relative: string } => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

  const formatted =
    date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    }) +
    " at " +
    date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

  let relative = "";
  if (diffInDays === 0) {
    relative = "Today";
  } else if (diffInDays === 1) {
    relative = "Yesterday";
  } else if (diffInDays < 7) {
    relative = `${diffInDays} days ago`;
  } else if (diffInDays < 30) {
    relative = `${Math.floor(diffInDays / 7)} weeks ago`;
  } else {
    relative = `${Math.floor(diffInDays / 30)} months ago`;
  }

  return { formatted, relative };
};

export const TicketTableView: React.FC<TicketTableViewProps> = ({ rows, selected, setSelected, order, setOrder, onRowClick, loading = false }) => {
  // Show empty state if no rows
  if (rows.length === 0 && !loading) {
    return (
      <Card
        variant="outlined"
        sx={{
          textAlign: "center",
          py: 6,
          borderStyle: "dashed",
          borderColor: "neutral.300",
        }}
      >
        <Stack spacing={2} alignItems="center">
          <SearchOffIcon sx={{ fontSize: "3rem", color: "neutral.400" }} />
          <Typography level="title-md" color="neutral">
            No tickets found
          </Typography>
          <Typography level="body-sm" color="neutral" sx={{ maxWidth: 400 }}>
            Try adjusting your search criteria or filters to find the tickets you're looking for.
          </Typography>
        </Stack>
      </Card>
    );
  }

  return (
    <Sheet
      className="TicketTableContainer"
      variant="outlined"
      sx={{
        display: { xs: "initial", sm: "initial" },
        width: "100%",
        borderRadius: "sm",
        flexShrink: 1,
        overflow: "auto",
        minHeight: 0,
        position: "relative",
        ...(loading && {
          opacity: 0.7,
          pointerEvents: "none",
        }),
      }}
    >
      <Table
        aria-labelledby="tableTitle"
        stickyHeader
        hoverRow
        sx={{
          "--TableCell-headBackground": "var(--joy-palette-background-level1)",
          "--Table-headerUnderlineThickness": "1px",
          "--TableRow-hoverBackground": "var(--joy-palette-background-level1)",
          "--TableCell-paddingY": "8px",
          "--TableCell-paddingX": "12px",
          "& thead th": {
            borderBottom: "2px solid",
            fontWeight: "600",
            fontSize: "0.875rem",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            color: "neutral.600",
          },
          "& tbody tr": {
            cursor: "pointer",
            transition: "all 0.15s ease",
            "&:hover": {
              transform: "translateY(-1px)",
              boxShadow: "0 2px 8px -2px rgba(0,0,0,0.1)",
              "& td": {
                borderColor: "primary.200",
              },
            },
          },
          "& td": {
            transition: "border-color 0.15s ease",
          },
        }}
      >
        <thead>
          <tr>
            <th style={{ width: 48, textAlign: "center", padding: "16px 8px" }}>
              <Tooltip title="Select all tickets">
                <Checkbox
                  size="sm"
                  indeterminate={selected.length > 0 && selected.length !== rows.length}
                  checked={selected.length === rows.length}
                  onChange={(event) => {
                    setSelected(event.target.checked ? rows.map((row) => row.Id) : []);
                  }}
                  color={selected.length > 0 || selected.length === rows.length ? "primary" : undefined}
                  sx={{ verticalAlign: "text-bottom" }}
                />
              </Tooltip>
            </th>
            <th style={{ width: 140, padding: "16px 8px" }}>
              <Link
                underline="none"
                color="primary"
                component="button"
                onClick={() => setOrder(order === "asc" ? "desc" : "asc")}
                endDecorator={<ArrowDropDownIcon />}
                sx={[
                  {
                    fontWeight: "600",
                    fontSize: "0.875rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    "& svg": {
                      transition: "0.2s",
                      transform: order === "desc" ? "rotate(0deg)" : "rotate(180deg)",
                    },
                  },
                  order === "desc" ? { "& svg": { transform: "rotate(0deg)" } } : { "& svg": { transform: "rotate(180deg)" } },
                ]}
              >
                Ticket #
              </Link>
            </th>
            <th style={{ width: 160, padding: "16px 8px" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <AccessTimeIcon sx={{ fontSize: "1rem", color: "neutral.500" }} />
                Last Updated
              </Box>
            </th>
            <th style={{ width: 120, padding: "16px 8px" }}>Status</th>
            <th style={{ width: 100, padding: "16px 8px" }}>Priority</th>
            <th style={{ minWidth: 200, padding: "16px 8px" }}>Subject</th>
            <th style={{ width: 120, padding: "16px 8px", display: "table-cell" }}>Type</th>
          </tr>
        </thead>
        <tbody>
          {[...rows].sort(getComparator(order, "Id")).map((row) => {
            const dateInfo = row.LastModifiedDate ? formatDate(row.LastModifiedDate) : null;

            return (
              <tr
                key={row.Id}
                onClick={(e) => {
                  // Don't trigger row click if clicking on checkbox
                  if ((e.target as HTMLElement).closest('input[type="checkbox"]')) return;
                  e.preventDefault();
                  onRowClick(row.Id);
                }}
              >
                <td style={{ textAlign: "center" }}>
                  <Tooltip title={`Select ticket ${row.CaseNumber}`}>
                    <Checkbox
                      size="sm"
                      checked={selected.includes(row.Id)}
                      color={selected.includes(row.Id) ? "primary" : undefined}
                      onChange={(event) => {
                        event.stopPropagation();
                        setSelected((ids) => (event.target.checked ? ids.concat(row.Id) : ids.filter((itemId) => itemId !== row.Id)));
                      }}
                      sx={{ verticalAlign: "text-bottom" }}
                    />
                  </Tooltip>
                </td>
                <td>
                  <Tooltip title={`View details for ticket ${row.CaseNumber}`}>
                    <Chip
                      sx={{
                        px: 2,
                        py: 1,
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        cursor: "pointer",
                        transition: "all 0.15s ease",
                        "&:hover": {
                          transform: "scale(1.05)",
                          boxShadow: "0 2px 8px -2px rgba(0,0,0,0.2)",
                        },
                      }}
                      variant="solid"
                      color="primary"
                      component={"span"}
                    >
                      {row.CaseNumber}
                    </Chip>
                  </Tooltip>
                </td>
                <td>
                  {dateInfo ? (
                    <Tooltip title={dateInfo.formatted}>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                        <Typography level="body-sm" sx={{ fontWeight: 500 }}>
                          {dateInfo.relative}
                        </Typography>
                        <Typography level="body-xs" color="neutral">
                          {dateInfo.formatted.split(" at ")[0]}
                        </Typography>
                      </Box>
                    </Tooltip>
                  ) : (
                    <Typography level="body-sm" color="neutral">
                      No updates yet
                    </Typography>
                  )}
                </td>
                <td>
                  <Tooltip title={`Status: ${row.Status}`}>
                    <Chip
                      sx={{
                        px: 2,
                        py: 1,
                        fontWeight: 500,
                        minWidth: "fit-content",
                      }}
                      variant="solid"
                      size="sm"
                      startDecorator={
                        {
                          Resolved: <CheckRoundedIcon />,
                          Closed: <CheckRoundedIcon />,
                          "In Progress": <AutorenewRoundedIcon />,
                          Pending: <AutorenewRoundedIcon />,
                          New: <ConfirmationNumberIcon />,
                          Open: <ConfirmationNumberIcon />,
                        }[row.Status]
                      }
                      color={
                        {
                          Resolved: "success",
                          Closed: "success",
                          "In Progress": "warning",
                          Pending: "warning",
                          New: "primary",
                          Open: "primary",
                        }[row.Status] as ColorPaletteProp
                      }
                      component={"span"}
                    >
                      {row.Status}
                    </Chip>
                  </Tooltip>
                </td>
                <td>
                  <Tooltip title={`Priority: ${row.Priority || "Medium"}`}>
                    <Chip
                      sx={{
                        px: 2,
                        py: 1,
                        fontWeight: 500,
                        minWidth: "fit-content",
                      }}
                      variant="soft"
                      size="sm"
                      color={
                        ({
                          Low: "neutral",
                          Medium: "primary",
                          High: "warning",
                          Urgent: "danger",
                        }[row.Priority] as ColorPaletteProp) || "neutral"
                      }
                      component={"span"}
                    >
                      {row.Priority || "Medium"}
                    </Chip>
                  </Tooltip>
                </td>
                <td>
                  <Tooltip title={row.Subject}>
                    <Typography
                      level="body-sm"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: { xs: 150, sm: 200, md: 300 },
                        fontWeight: 500,
                        lineHeight: 1.4,
                      }}
                    >
                      {row.Subject}
                    </Typography>
                  </Tooltip>
                </td>
                <td style={{ display: { xs: "none", md: "table-cell" } } as any}>
                  <Typography
                    level="body-sm"
                    color="neutral"
                    sx={{
                      fontWeight: 400,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.Incident_type__c || "Support Request"}
                  </Typography>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </Sheet>
  );
};
